// Distribution of the answer by the registry (the last step of the correspondence workflow): dispatch date, recipient,
// method and resolution → the document becomes "Finalizat" and the register's "ieșire" fields are filled in.
import React, { useMemo, useState } from 'react';
import { api } from '../api';
import type { Attribute, Doc } from '../types';
import { Field, Modal, fmtDate, fmtDateTime, useToast } from '../ui';
import { Icon } from '../ui/Icon';
import { sectionDirection } from './Response';

const METHODS = ['Poștă', 'E-mail', 'Personal', 'Curier', 'Fax', 'Altul'];

export function DistributeModal({ doc, attrs, onClose, onDone }: { doc: Doc; attrs: Attribute[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  // the answer usually goes back to whoever sent the document
  const sender = useMemo(() => {
    const a = attrs.find(x => sectionDirection(x.section) !== 'OUT' && (x.code === 'expeditor' || /de la cine|expeditor|petiționar|petitionar|solicitant/i.test(x.name) || /petitionar|solicitant/.test(x.code)));
    const v = a ? doc.metadata[a.code] : null;
    if (v == null || v === '') return '';
    return a?.items?.find(i => String(i.value) === String(v))?.label || String(v);
  }, [attrs, doc]);
  const recipients = useMemo(() => {
    const a = attrs.find(x => sectionDirection(x.section) === 'OUT' && x.type === 'LIST' && (/către|catre/i.test(x.name) || x.code.includes('destinatar')));
    return (a?.items || []).map(i => i.label || i.value);
  }, [attrs]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [recipient, setRecipient] = useState(sender);
  const [method, setMethod] = useState('Poștă');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/api/documents/${doc.id}/distribute`, { date, recipient: recipient.trim(), method, note: note.trim() });
      toast('Documentul a fost distribuit și finalizat', 'ok'); onDone();
    } catch (e: any) { toast(e.message, 'error'); } finally { setBusy(false); }
  };
  const answers = (doc.assignments || []).filter(a => a.status === 'RESOLVED');
  return (
    <Modal title="Distribuie documentul" icon="send" onClose={onClose}
      footer={<><span className="grow" /><button className="btn" onClick={onClose}>Renunță</button><button className="btn primary" disabled={busy || !date} onClick={go}><Icon name="send" />Distribuie și finalizează</button></>}>
      <div className="stack" style={{ gap: 14 }}>
        <div className="banner small"><Icon name="info" size={15} /><span>{answers.length ? <>Toate compartimentele au rezolvat ({answers.map(a => a.departmentName || a.userName).join(', ')}). </> : null}Înregistrați trimiterea răspunsului; documentul trece la <b>Finalizate</b>, iar în registru se completează data ieșirii și destinatarul.</span></div>
        <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <Field label="Data ieșirii / expedierii" required><input className="input" type="date" value={date} onChange={e => setDate(e.target.value)} /></Field>
          <Field label="Către cine s-a trimis">
            <input className="input" list="dist-recipients" value={recipient} onChange={e => setRecipient(e.target.value)} placeholder="Destinatarul răspunsului" />
            <datalist id="dist-recipients">{[...new Set([sender, ...recipients].filter(Boolean))].map(r => <option key={r} value={r} />)}</datalist>
          </Field>
        </div>
        <Field label="Modul de transmitere">
          <div className="chips">{METHODS.map(m => <button key={m} type="button" className={'chip-btn' + (method === m ? ' on' : '')} onClick={() => setMethod(m)}>{m}</button>)}</div>
        </Field>
        <Field label="Rezolvarea / observații (opțional)"><textarea className="textarea" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="ex. Răspuns transmis cu adresa nr. …" /></Field>
      </div>
    </Modal>
  );
}

/** "Distribuire" block in the document details. */
export function DistributionInfo({ doc }: { doc: Doc }) {
  const d = doc.distribution;
  if (!d) return null;
  return (
    <>
      <div className="section-title">Distribuire</div>
      <dl className="kv">
        <dt>Data ieșirii</dt><dd>{fmtDate(d.date)}</dd>
        {d.recipient && <><dt>Către</dt><dd>{d.recipient}</dd></>}
        {d.method && <><dt>Mod</dt><dd>{d.method}</dd></>}
        {d.note && <><dt>Rezolvarea</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{d.note}</dd></>}
        <dt>Distribuit de</dt><dd>{d.byName} {d.at && <span className="muted small">{fmtDateTime(d.at)}</span>}</dd>
      </dl>
    </>
  );
}
